import { getSession, signIn } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

function formatApiErrorDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object' && 'msg' in item) {
          const msg = (item as { msg?: string }).msg
          const loc = (item as { loc?: unknown[] }).loc
          if (msg && Array.isArray(loc) && loc.length > 0) {
            return `${loc.join('.')}: ${msg}`
          }
          return msg || JSON.stringify(item)
        }
        return JSON.stringify(item)
      })
      .join('; ')
  }
  if (detail && typeof detail === 'object') {
    return JSON.stringify(detail)
  }
  // Empty string signals "no usable detail" so the caller can fall back to
  // something that identifies the failure (see the status-code fallback below).
  return ''
}

// Guard to prevent multiple parallel 401 responses from each triggering
// their own signIn('keycloak') redirect (redirect storm).
let isRedirecting = false

interface ApiClientOptions extends RequestInit {
  responseType?: 'json' | 'blob' | 'text'
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.) with optional responseType.
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: ApiClientOptions = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    if (!isRedirecting) {
      isRedirecting = true
      // Force sign-in to get a new, valid session.
      await signIn('keycloak')
    }
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const { responseType = 'json', ...fetchOptions } = options
  const response = await fetch(fullUrl, { ...fetchOptions, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session (only once to prevent redirect storms).
  if (response.status === 401) {
    if (!isRedirecting) {
      isRedirecting = true
      await signIn('keycloak')
    }
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    // Unhandled backend exceptions come back as plain text, not JSON, so the parse
    // fails here. Do not fall back to response.statusText: HTTP/2 (Cloud Run) always
    // reports it as an empty string, which is what produced a bare "An API error
    // occurred." with no way to tell a 500 from a 403.
    const errorData = await response.json().catch(() => null)
    const detail = errorData ? formatApiErrorDetail(errorData.detail) : ''
    throw new Error(
      detail || `An API error occurred (HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}).`
    )
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  // Handle different response types
  if (responseType === 'blob') {
    return response.blob() as Promise<T>
  } else if (responseType === 'text') {
    return response.text() as Promise<T>
  } else {
    return response.json() as Promise<T>
  }
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: ApiClientOptions): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  /**
   * Upload one or more files using multipart/form-data.
   */
  upload: <T = unknown>(endpoint: string, file: File): Promise<T> => {
    const formData = new FormData()
    formData.append('file', file)

    return apiClientFetch<T>(endpoint, {
      method: 'POST',
      body: formData,
    })
  },

  uploadMultiple: <T = unknown>(endpoint: string, files: File[]): Promise<T> => {
    const formData = new FormData()
    for (const file of files) {
      formData.append('files', file)
    }

    return apiClientFetch<T>(endpoint, {
      method: 'POST',
      body: formData,
    })
  },
}

// Default export for backward compatibility
export default apiClientFetch
